import React, { FormEvent, Suspense, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CircleHelp, Loader2, Send, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSupportPanel } from '../../contexts/SupportPanelContext';
import { importWithChunkRecovery } from '../../services/appUpdate';
import { MarkdownLink } from '../Markdown/MarkdownLink';
import type { ChatMessage } from '../../services/customerSupportBot';
import { submitSupportRequest } from '../../services/support';

const WELCOME_MESSAGE: ChatMessage = {
  role: 'assistant',
  content: "Hello! I'm your support assistant. How can I help you today?",
};

// Recovers from a stale build the same way route chunks do; a vendor chunk is
// just as likely to disappear under an open tab after a deploy.
const ReactMarkdown = React.lazy(() => importWithChunkRecovery(() => import('react-markdown')));

function MessageContent({ content }: { content: string }) {
  return (
    <Suspense fallback={<p>{content}</p>}>
      {/* A support answer's links open in a new tab so following one doesn't
          navigate away from — and close — the chat panel. */}
      <ReactMarkdown components={{ a: MarkdownLink }}>{content}</ReactMarkdown>
    </Suspense>
  );
}

export function SupportBotButton() {
  const { userAccount } = useAuth();
  // Open state lives in context so copy elsewhere that says "contact support"
  // can actually take the expert there, on the right view.
  const { isOpen: open, view, openSupport, closeSupport, setView } = useSupportPanel();
  const [chatId, setChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [escalated, setEscalated] = useState(false);
  // The Flutter SupportFormDialog offered a bot conversation AND a direct
  // `submitForm` path to a human. Only the bot half was ported, so the sole route
  // to a person was escalating a conversation you had to start first — no use to
  // someone who simply wants to report a problem.
  const [formMessage, setFormMessage] = useState('');
  const [formSent, setFormSent] = useState(false);
  const [formBusy, setFormBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) return;
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    inputRef.current?.focus();
  }, [open, messages]);

  const ensureChat = async (): Promise<string | null> => {
    if (chatId) return chatId;
    if (!userAccount) return null;
    const { createCustomerSupportChat } = await import('../../services/customerSupportBot');
    const chat = await createCustomerSupportChat(userAccount);
    setChatId(chat.id);
    return chat.id;
  };

  const sendMessage = async (e: FormEvent) => {
    e.preventDefault();
    const content = input.trim();
    if (!content || busy) return;

    setBusy(true);
    setError(null);
    setInput('');
    const userMessage: ChatMessage = { role: 'user', content };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);

    try {
      const id = await ensureChat();
      const { sendCustomerSupportBotMessage, updateChatMessages } = await import('../../services/customerSupportBot');
      const response = await sendCustomerSupportBotMessage(content, nextMessages);
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response || "I don't have an answer for that yet. Please try rephrasing your question.",
      };
      setMessages((current) => [...current, assistantMessage]);
      if (id) await updateChatMessages(id, content, assistantMessage.content);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send your message. Please try again.');
      setMessages((current) => [
        ...current,
        {
          role: 'assistant',
          content: "I'm having trouble processing your request. Please try again or escalate this conversation to support.",
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const escalate = async () => {
    if (busy || escalated) return;
    setBusy(true);
    setError(null);
    try {
      const { escalateCustomerSupportChat } = await import('../../services/customerSupportBot');
      await escalateCustomerSupportChat(chatId, messages, userAccount?.firstNamePreferred ?? userAccount?.firstName);
      setEscalated(true);
      setMessages((current) => [
        ...current,
        {
          role: 'assistant',
          content:
            "I've escalated this conversation to our support team. You will receive an email confirmation, and our team will respond within 1-2 business days.",
        },
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not escalate to support.');
    } finally {
      setBusy(false);
    }
  };

  const submitMessage = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = formMessage.trim();
    if (!trimmed || formBusy) return;
    setFormBusy(true);
    setError(null);
    try {
      await submitSupportRequest(trimmed, userAccount?.firstNamePreferred ?? userAccount?.firstName);
      setFormSent(true);
      setFormMessage('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send your message.');
    } finally {
      setFormBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => openSupport()}
        aria-label="Open support"
        className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white/85 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/50"
      >
        <CircleHelp className="h-5 w-5" />
      </button>

      {open
        ? createPortal(
            <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/30 print:hidden" role="dialog" aria-modal="true" aria-label="Support">
              <button type="button" aria-label="Close support backdrop" className="flex-1 cursor-default" onClick={closeSupport} />
              <section className="flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
                <div className="flex h-16 items-center justify-between border-b border-slate-200 px-4">
                  <div className="flex items-center gap-1" role="tablist" aria-label="Support options">
                    {(['chat', 'message'] as const).map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        role="tab"
                        aria-selected={view === tab}
                        onClick={() => setView(tab)}
                        className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                          view === tab ? 'bg-primary/10 text-primary' : 'text-slate-500 hover:bg-slate-100'
                        }`}
                      >
                        {tab === 'chat' ? 'Support Chat' : 'Send a message'}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={closeSupport}
                    aria-label="Close support"
                    className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {view === 'message' ? (
                  <div className="min-h-0 flex-1 overflow-y-auto bg-white px-4 py-4">
                    {formSent ? (
                      <p className="rounded bg-green-50 px-3 py-2 text-sm text-green-700">
                        Thanks — we&apos;ve received your message and will be in touch by email.
                      </p>
                    ) : (
                      <form onSubmit={submitMessage} className="flex flex-col gap-3">
                        <label htmlFor="support-message" className="text-sm text-slate-600">
                          Tell us what you need help with and we&apos;ll reply by email.
                        </label>
                        {/* Name and email are deliberately not collected: the
                            submitSupportRequest function resolves the caller's
                            identity from their token, so asking again would only
                            invite a typo in the address we reply to. */}
                        <textarea
                          id="support-message"
                          value={formMessage}
                          onChange={(e) => setFormMessage(e.target.value)}
                          placeholder="How can we help?"
                          rows={8}
                          maxLength={8000}
                          className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                        <button
                          type="submit"
                          disabled={formBusy || formMessage.trim() === ''}
                          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover disabled:opacity-60"
                        >
                          {formBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                          Send message
                        </button>
                        {error ? (
                          <p role="alert" className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
                            {error}
                          </p>
                        ) : null}
                      </form>
                    )}
                  </div>
                ) : (
                <>
                <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto bg-slate-50 px-4 py-4">
                  <div className="flex flex-col gap-3">
                    {messages.map((message, index) => (
                      <div key={`${message.role}-${index}`} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div
                          className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-6 shadow-sm ${
                            message.role === 'user' ? 'bg-primary/10 text-slate-900' : 'bg-white text-slate-800'
                          }`}
                        >
                          <MessageContent content={message.content} />
                        </div>
                      </div>
                    ))}
                    {busy ? (
                      <div className="flex justify-start">
                        <div className="rounded-2xl bg-white px-4 py-3 text-sm text-slate-500 shadow-sm">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="border-t border-slate-200 bg-slate-50 px-4 py-2 text-center">
                  {!escalated ? (
                    <button type="button" onClick={() => void escalate()} disabled={busy} className="text-xs font-medium text-primary hover:underline disabled:opacity-50">
                      Escalate to Support (1-2 day response time)
                    </button>
                  ) : null}
                  {error ? (
                    <p role="alert" className="mt-2 rounded bg-red-50 px-3 py-2 text-left text-xs text-red-700">
                      {error}
                    </p>
                  ) : null}
                </div>

                <form onSubmit={sendMessage} className="flex items-end gap-3 border-t border-slate-200 bg-white p-4">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        void sendMessage(e);
                      }
                    }}
                    rows={1}
                    maxLength={2000}
                    placeholder="Type your message..."
                    className="max-h-28 min-h-10 flex-1 resize-none rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <button
                    type="submit"
                    disabled={busy || !input.trim()}
                    aria-label="Send support message"
                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary text-white hover:bg-primary-hover disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </button>
                </form>
                </>
                )}
              </section>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
